import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GatewayDevice, InstallationFeaturesStore, WallboxChargingModes } from '@store';
import {
  ActivateDeactivateCommands,
  ActiveModeCommands,
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureNames,
  GatewayDevicesProperties,
  IsActiveProperty,
  PVExcessDeviceType,
  PVExcessOutflowTypes,
  PVExcessPriorityListItem,
  PhotovoltaicPriorityList,
  PhotovoltaicPriorityListCommands,
  PhotovoltaicPriorityListProperties,
  PropertiesStringValueGeneric,
} from '@vi-iot/client/index';
import { ViToastService } from '@vi-iot/ui/index';
import {
  Observable,
  combineLatest,
  first,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { PVPowerPrioritization } from './pv-power-prioritization-util';
import { EnergyFlowOutType } from 'src/app/operational-data-common/translation-pipes/translate-energy-flow-type.pipe';
import { DevicesServiceModels } from 'src/app/@store/devices/devices.service.models';
import { DevicesService } from 'src/app/@store/devices/devices.service';

@Injectable({
  providedIn: 'root',
})
export class PVPowerPrioritizationService {
  private readonly gatewayDevices$ = this.installationFeaturesStore
    .selectInstallationGatewayFeature(FeatureNames.gatewayDevices)
    .pipe(
      map((feature: Feature<GatewayDevicesProperties, never>) => feature.properties.devices.value)
    );

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private viToastService: ViToastService,
    private translateService: TranslateService,
    private devicesService: DevicesService
  ) {}

  getListOfAvailablePVExcessDevicesDetails(
    featureNmae: string
  ): Observable<
    [
      Feature<PhotovoltaicPriorityListProperties, PhotovoltaicPriorityListCommands>,
      Feature<DeviceNameProperties, DeviceNameCommands>[],
      { id: string; type: PVExcessDeviceType }[],
      { id: string; isActive: boolean }[],
      DevicesServiceModels.WallboxDeviceWithUIName[]
    ]
  > {
    const devicesPriorityDetails$ = this.getDevicePrioritizationOrder(featureNmae);
    const devicesNames$ = this.getInstallationDeviceNames();
    const deviceTypes$ = this.getAllAvailableDevicesWithTypes();
    const deviceIdMapToDeviceOptimizationStatus$ = this.getDevicesOptimisationStatus();
    const wallboxDevicesWithUInames$ = this.devicesService.wallboxDevicesWithUInames$;

    return combineLatest([
      devicesPriorityDetails$,
      devicesNames$,
      deviceTypes$,
      deviceIdMapToDeviceOptimizationStatus$,
      wallboxDevicesWithUInames$,
    ]);
  }

  formatDeviceDetails(
    pvExcessList: Feature<PhotovoltaicPriorityListProperties, PhotovoltaicPriorityListCommands>,
    deviceNames: Feature<DeviceNameProperties, DeviceNameCommands>[],
    types: { id: string; type: PVExcessDeviceType }[],
    devicIdMapToDeviceOptimizationStatus: { id: string; isActive: boolean }[],
    wallboxDevicesWithNames: DevicesServiceModels.WallboxDeviceWithUIName[]
  ): PVExcessPriorityListItem[] {
    return pvExcessList.properties.devices.value
      .map((pvDevice: { controller: string; priority: number }) => {
        const type = types.find((t) => t.id === pvDevice.controller)?.type;
        const name = this.formatDeviceName(deviceNames, pvDevice, type, wallboxDevicesWithNames);
        const isOptimizationActive =
          devicIdMapToDeviceOptimizationStatus.find(
            (device: { id: string; isActive: boolean }) => device.id === pvDevice.controller
          )?.isActive ?? false;
        const isWarningIconVisible = PVPowerPrioritization.isWarningIconVisible(
          type,
          isOptimizationActive
        );
        const infoKey = PVPowerPrioritization.getInfoKey(type);
        const subTitleKey = PVPowerPrioritization.getSubTitleKey(type, isOptimizationActive);

        return {
          name,
          priority: pvDevice.priority,
          type,
          isWarningIconVisible,
          deviceId: pvDevice.controller,
          isLocked: false,
          canMoveUp: true,
          canMoveDown: true,
          infoKey,
          subTitleKey,
        };
      })
      .sort((a, b) => a.priority - b.priority);
  }

  formatDeviceName(
    deviceNames: Feature<DeviceNameProperties, DeviceNameCommands>[],
    pvDevice: PhotovoltaicPriorityList,
    type: PVExcessDeviceType,
    wallboxDevicesWithUInames: DevicesServiceModels.WallboxDeviceWithUIName[]
  ): string {
    const featureName = deviceNames.find(
      (deviceNameFeature: Feature<DeviceNameProperties, DeviceNameCommands>) =>
        deviceNameFeature?.deviceId === pvDevice.controller
    )?.properties.name.value;

    if (featureName) {
      return featureName;
    }

    if (type === EnergyFlowOutType.VCS) {
      const matchingName = wallboxDevicesWithUInames.find(
        (device) => device.device.id === pvDevice.controller
      ).name;

      if (matchingName) {
        return matchingName;
      }
    }

    return this.getUnknownDeviceName(
      type,
      type === EnergyFlowOutType.DryContactGeneric
        ? pvDevice?.controller?.split('-')[1]
        : pvDevice?.controller
    );
  }

  getDevicePrioritizationOrder(
    featureName: string
  ): Observable<Feature<PhotovoltaicPriorityListProperties, PhotovoltaicPriorityListCommands>> {
    return this.installationFeaturesStore
      .selectInstallationHemsFeature<
        PhotovoltaicPriorityListProperties,
        PhotovoltaicPriorityListCommands
      >(featureName)
      .pipe(
        tap((feature) => {
          if (featureName === FeatureNames.pvExcessPowerPrioritizationDefault && !feature) {
            this.viToastService.error({
              message: this.translateService.instant(
                '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.RESTORE_DEFAULTS_ERROR'
              ),
            });
          }
        })
      );
  }

  getAllAvailableDevicesWithTypes(): Observable<{ id: string; type: PVExcessDeviceType }[]> {
    return this.gatewayDevices$.pipe(
      map((devices: GatewayDevice[]) =>
        devices
          .map((device: GatewayDevice) => ({
            id: device.id,
            type: PVPowerPrioritization.getDeviceType(device),
          }))
          .filter((device) => !!device.type)
      )
    );
  }

  getDevicesOptimisationStatus(): Observable<{ id: string; isActive: boolean }[]> {
    return this.getAllAvailableDevicesWithTypes().pipe(
      switchMap((devices: { id: string; type: PVExcessDeviceType }[]) => {
        const optimizationStatusObservables = devices.map((device) =>
          this.getOptimizationDetails(device).pipe(
            first(),
            map((feature) => ({
              id: device.id,
              isActive: feature?.properties?.active?.value ?? false,
            }))
          )
        );

        return combineLatest(optimizationStatusObservables);
      })
    );
  }

  getInstallationDeviceNames(): Observable<Feature<DeviceNameProperties, DeviceNameCommands>[]> {
    return this.getDevicePrioritizationOrder(FeatureNames.pvExcessPowerPrioritizationDefault).pipe(
      mergeMap((feature) => {
        const deviceOrder = feature.properties.devices.value;

        return combineLatest(
          deviceOrder.map((device: PhotovoltaicPriorityList) =>
            this.getInstallationDeviceName(device)
          )
        );
      })
    );
  }

  getInstallationDeviceName(
    device: PhotovoltaicPriorityList
  ): Observable<Feature<DeviceNameProperties, DeviceNameCommands>> {
    return this.installationFeaturesStore
      .selectInstallationDeviceFeature<DeviceNameProperties, DeviceNameCommands>(
        FeatureNames.deviceName,
        device.controller
      )
      .pipe(startWith(null));
  }

  getOptimizationDetails({
    id,
    type,
  }: {
    id: string;
    type: PVExcessDeviceType;
  }): Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>> {
    const defaultValue = {
      properties: {
        active: {
          value: true,
        },
      },
    } as Feature<IsActiveProperty, ActivateDeactivateCommands>;

    switch (type) {
      case PVExcessOutflowTypes.OBNG_HEATPUMP:
      case EnergyFlowOutType.Heatpump:
        return this.getHeatpumpOptimizationActiveStatus();
      case PVExcessOutflowTypes.Battery:
        return this.selectInstallationHemsFeature(FeatureNames.forecastBasedBatteryOperation);
      case EnergyFlowOutType.PhotovoltaicsHeater:
        return this.selectInstallationHemsFeature(FeatureNames.photovoltaicExcessPvHandling);
      case EnergyFlowOutType.DryContactGeneric:
        const hocaFeatureNameForDryContact = PVPowerPrioritization.mapHemsIdToHocaFeatureName(id);

        return this.selectInstallationHemsFeature(
          FeatureNames.emsEnergyPVExcessToDryContact(hocaFeatureNameForDryContact)
        );
      case EnergyFlowOutType.VCS:
        return this.getWallboxOptimizationActiveStatus(id);

      default:
        return of(defaultValue);
    }
  }

  getHeatpumpOptimizationActiveStatus(): Observable<
    Feature<IsActiveProperty, ActivateDeactivateCommands>
  > {
    return combineLatest([
      this.selectInstallationHemsFeature(FeatureNames.photovoltaicExcessHeatpumpHandling),
      this.selectInstallationHemsFeature(FeatureNames.emsHocaFeaturesHeatpumpOnly),
    ]).pipe(
      map(([pvExcesstoHeatPump, emsHocaHeatPumponly]) =>
        PVPowerPrioritization.getHeatpumpOptimizationActiveStatus(
          pvExcesstoHeatPump,
          emsHocaHeatPumponly
        )
      )
    );
  }

  getWallboxOptimizationActiveStatus(
    deviceId: string
  ): Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>> {
    return this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        PropertiesStringValueGeneric<WallboxChargingModes>,
        ActiveModeCommands
      >(FeatureNames.wallboxChargingMode, deviceId)
      .pipe(
        map(
          (
            feature: Feature<PropertiesStringValueGeneric<WallboxChargingModes>, ActiveModeCommands>
          ) => {
            const mode = feature?.properties?.value?.value;
            const isGreenModeActive = mode === WallboxChargingModes.GREEN;

            return {
              properties: {
                active: {
                  value: isGreenModeActive,
                },
              },
            } as Feature<IsActiveProperty, ActivateDeactivateCommands>;
          }
        )
      );
  }

  selectInstallationHemsFeature(
    featureName: string
  ): Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>> {
    return this.installationFeaturesStore.selectInstallationHemsFeature<
      IsActiveProperty,
      ActivateDeactivateCommands
    >(featureName);
  }

  getUnknownDeviceName(type: PVExcessDeviceType, id: string): string {
    const translationKey = PVPowerPrioritization.LIST_DEVICE_ITEM_KEYS.DEVICE_NAMES[type];
    if (!translationKey) {
      return this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.UNKNOWN'
      );
    }
    if (type === EnergyFlowOutType.DryContactGeneric) {
      return this.translateService.instant(translationKey) + ' ' + id;
    }

    return this.translateService.instant(translationKey);
  }
}
